import { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

interface HoverCardProps {
  children: React.ReactNode;
  content: React.ReactNode | ((props: { close: () => void }) => React.ReactNode);
  anchor?: 'right start' | 'bottom start' | 'top start';
  enterDelay?: number;
  leaveDelay?: number;
  className?: string;
}

const GAP = 8;

export function HoverCard({
  children,
  content,
  anchor = 'right start',
  enterDelay = 400,
  leaveDelay = 150,
  className,
}: HoverCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState<React.CSSProperties>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const enterTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
    },
    []
  );

  const close = useCallback(() => setIsOpen(false), []);

  const scheduleOpen = useCallback(() => {
    clearTimeout(leaveTimer.current);
    enterTimer.current = setTimeout(() => {
      const el = triggerRef.current?.firstElementChild ?? triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (anchor === 'top start') {
        const bottomFromViewport = window.innerHeight - r.top + GAP;
        setPos({ bottom: bottomFromViewport, left: r.left });
      } else if (anchor === 'bottom start') {
        setPos({ top: r.bottom + GAP, left: r.left });
      } else {
        setPos({ top: r.top, left: r.right + GAP });
      }
      setIsOpen(true);
    }, enterDelay);
  }, [enterDelay, anchor]);

  const scheduleClose = useCallback(() => {
    clearTimeout(enterTimer.current);
    leaveTimer.current = setTimeout(close, leaveDelay);
  }, [leaveDelay, close]);

  const cancelClose = useCallback(() => {
    clearTimeout(leaveTimer.current);
  }, []);

  return (
    <div ref={triggerRef} onMouseEnter={scheduleOpen} onMouseLeave={scheduleClose}>
      {children}
      {isOpen &&
        createPortal(
          <div
            className={clsx(
              'fixed z-50 isolate w-max rounded-md overflow-y-auto',
              'bg-popover/75 backdrop-blur-xl shadow-lg ring-1 ring-border dark:ring-inset',
              'animate-in fade-in duration-100',
              className
            )}
            style={pos}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {typeof content === 'function' ? content({ close }) : content}
          </div>,
          document.body
        )}
    </div>
  );
}
